import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import UnderlineExt from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import TextAlign from "@tiptap/extension-text-align";
import ImageExt from "@tiptap/extension-image";
import LinkExt from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import mjml2html from "mjml-browser";
import {
  editorColors as colors,
  fonts,
  MERGE_TAGS,
  CONTACT_TYPES,
} from "../styles/theme";
import { getTemplates } from "../data/templates";
import { useTenant } from "../contexts/TenantContext";
import {
  fetchContacts,
  deriveContactType,
  uploadImage,
  saveNewsletterDraft,
  fetchNewsletterDraft,
  testSendNewsletter,
  fetchAudienceLists,
  resolveAudienceList,
  fetchSystemSettings,
  fetchWebPages,
  fetchZoomEvents,
} from "../services/api";
import { getGraphToken } from "../services/msal";
import { MergeTagNode } from "../extensions/MergeTagNode";
import { YouTubeNode, extractYouTubeId } from "../extensions/YouTubeNode";
import { SpamHighlight } from "../extensions/SpamHighlight";
import { ButtonNode } from "../extensions/ButtonNode";
import { PollNode } from "../extensions/PollNode";
import "../styles/editor.css";

import { resizeImageForEmail } from "../features/editor/resizeImage";
import EditorTopBar from "../features/editor/EditorTopBar";
import EditorSidebar from "../features/editor/EditorSidebar";
import EditorToolbar from "../features/editor/EditorToolbar";
import EditorPreview from "../features/editor/EditorPreview";
import EditorStatusBar from "../features/editor/EditorStatusBar";
import CtaButtonModal from "../features/editor/CtaButtonModal";
import SendConfirmModal from "../features/editor/SendConfirmModal";

// ============================================================
// Main Editor
// ============================================================
export default function Editor() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const iframeRef = useRef(null);
  const tenant = useTenant();
  const templates = getTemplates(tenant);
  const [title, setTitle] = useState("");
  const [subject, setSubject] = useState(templates[1].subject);
  const [preheader, setPreheader] = useState("");
  const [previewMode, setPreviewMode] = useState(null);
  const [showMergeTags, setShowMergeTags] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(templates[1]);
  const [segment, setSegment] = useState("test");
  const [customEmails, setCustomEmails] = useState([]);
  const [customEmailInput, setCustomEmailInput] = useState("");
  const [wordCount, setWordCount] = useState(0);
  const [selectionKey, setSelectionKey] = useState(0);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [draftId, setDraftId] = useState(searchParams.get("draft") || null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [testSent, setTestSent] = useState(null);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [spacing, setSpacing] = useState(null);
  const [ctaModal, setCtaModal] = useState(null);
  const [ctaPages, setCtaPages] = useState(null);
  const [audienceLists, setAudienceLists] = useState([]);
  const [segmentCounts, setSegmentCounts] = useState({
    test: "6",
    all: "…",
    ...Object.fromEntries(Object.keys(CONTACT_TYPES).map((k) => [k, "…"])),
    digest: "…",
  });

  // TipTap editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      UnderlineExt,
      TextStyle,
      Color,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      ImageExt.configure({
        inline: false,
        allowBase64: false,
        HTMLAttributes: { style: "max-width:100%;height:auto;display:block;" },
      }),
      LinkExt.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder: "Start writing your newsletter..." }),
      MergeTagNode,
      YouTubeNode,
      ButtonNode,
      PollNode,
      SpamHighlight,
    ],
    content: templates[1].defaultContent,
    editorProps: {
      attributes: { spellcheck: "true" },
      handleDrop: (view, event, slice, moved) => {
        if (moved) return false;
        const files = event.dataTransfer?.files;
        if (!files?.length) return false;
        const imageFile = Array.from(files).find((f) => f.type.startsWith("image/"));
        if (!imageFile) return false;
        if (imageFile.size > 5 * 1024 * 1024) { alert("Image must be under 5 MB"); return true; }
        const coordinates = view.posAtCoords({ left: event.clientX, top: event.clientY });
        setUploading(true);
        resizeImageForEmail(imageFile)
          .then((resized) => uploadImage(resized))
          .then(({ url }) => {
            const node = view.state.schema.nodes.image.create({ src: url, alt: imageFile.name });
            const pos = coordinates?.pos ?? view.state.selection.from;
            view.dispatch(view.state.tr.insert(pos, node));
          })
          .catch((err) => alert(`Upload failed: ${err.message}`))
          .finally(() => setUploading(false));
        return true;
      },
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;
        const imageItem = Array.from(items).find((i) => i.type.startsWith("image/"));
        if (!imageItem) return false;
        const file = imageItem.getAsFile();
        if (!file) return false;
        if (file.size > 5 * 1024 * 1024) { alert("Image must be under 5 MB"); return true; }
        event.preventDefault();
        setUploading(true);
        resizeImageForEmail(file)
          .then((resized) => uploadImage(resized))
          .then(({ url }) => {
            const node = view.state.schema.nodes.image.create({ src: url, alt: file.name || "pasted-image" });
            view.dispatch(view.state.tr.replaceSelectionWith(node));
          })
          .catch((err) => alert(`Upload failed: ${err.message}`))
          .finally(() => setUploading(false));
        return true;
      },
    },
    onUpdate: ({ editor: ed }) => {
      const text = ed.getText();
      setWordCount(text.trim().split(/\s+/).filter(Boolean).length);
      setSelectionKey((k) => k + 1);
    },
    onSelectionUpdate: () => setSelectionKey((k) => k + 1),
  });

  // Fetch real contact counts and audience lists on mount
  useEffect(() => {
    fetchContacts({ limit: 5000 })
      .then((data) => {
        const list = data.contacts || [];
        const counts = {
          test: "6",
          all: (data.total || list.length).toLocaleString(),
          digest: list.filter((c) => c.emailFrequency === "digest").length.toLocaleString(),
        };
        for (const typeId of Object.keys(CONTACT_TYPES)) {
          counts[typeId] = list.filter((c) => deriveContactType(c.tags, c.ghlTags) === typeId).length.toLocaleString();
        }
        setSegmentCounts((prev) => ({ ...prev, ...counts }));
      })
      .catch(() => {});

    fetchSystemSettings()
      .then((s) => { if (s?.newsletterSpacing) setSpacing(s.newsletterSpacing); })
      .catch(() => {});

    fetchAudienceLists()
      .then((data) => {
        const lists = data.lists || [];
        setAudienceLists(lists);
        lists.forEach((l) => {
          const resolve = () =>
            resolveAudienceList(l.id, { preview: true })
              .then((r) => setSegmentCounts((prev) => ({ ...prev, [`list:${l.id}`]: r.total.toLocaleString() })));
          resolve().catch(() => setTimeout(() => resolve().catch(() => {}), 3000));
        });
      })
      .catch(() => {});
  }, []);

  // Template switch (skip if loading a draft)
  useEffect(() => {
    if (editor && selectedTemplate && !draftLoaded) {
      editor.commands.setContent(selectedTemplate.defaultContent);
      const text = editor.getText();
      setWordCount(text.trim().split(/\s+/).filter(Boolean).length);
    }
  }, [selectedTemplate, editor, draftLoaded]);

  // Load draft from URL param on mount
  useEffect(() => {
    const id = searchParams.get("draft");
    if (!id || !editor) return;
    fetchNewsletterDraft(id)
      .then((draft) => {
        setDraftId(draft.id);
        setTitle(draft.title || "");
        setSubject(draft.subject || "");
        setPreheader(draft.preheader || "");
        setSegment(draft.segment || "test");
        if (draft.customEmails?.length) setCustomEmails(draft.customEmails);
        if (draft.bodyHtml) {
          editor.commands.setContent(draft.bodyHtml);
          const text = editor.getText();
          setWordCount(text.trim().split(/\s+/).filter(Boolean).length);
        }
        const tmpl = templates.find((t) => t.id === draft.templateId);
        if (tmpl) setSelectedTemplate(tmpl);
        setDraftLoaded(true);
      })
      .catch((err) => console.error("Failed to load draft:", err));
  }, [editor]);

  // Merge tag insertion
  const insertMergeTag = useCallback(
    (tag) => {
      if (!editor) return;
      const cleanTag = tag.replace(/[{}]/g, "");
      const mt = MERGE_TAGS.find((m) => m.tag === tag);
      editor.chain().focus().insertContent({
        type: "mergeTag",
        attrs: { tag: cleanTag, label: mt?.label || cleanTag },
      }).run();
    },
    [editor],
  );

  // Link insertion
  const insertLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("Enter URL:", previousUrl || "https://");
    if (url === null) return;
    if (url === "") { editor.chain().focus().unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url, target: "_blank" }).run();
  }, [editor]);

  // Image upload handler
  const handleImageUpload = useCallback(
    async (file) => {
      if (!editor || !file) return;
      if (!file.type.startsWith("image/")) return;
      if (file.size > 5 * 1024 * 1024) { alert("Image must be under 5 MB"); return; }
      setUploading(true);
      try {
        const resized = await resizeImageForEmail(file);
        const { url } = await uploadImage(resized);
        editor.chain().focus().setImage({ src: url, alt: file.name }).run();
      } catch (err) {
        alert(`Upload failed: ${err.message}`);
      } finally {
        setUploading(false);
      }
    },
    [editor],
  );

  // Image from file picker
  const handleInsertImage = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/jpeg,image/png,image/gif,image/webp";
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (file) handleImageUpload(file);
    };
    input.click();
  }, [handleImageUpload]);

  // YouTube embed
  const handleInsertYouTube = useCallback(() => {
    if (!editor) return;
    const url = window.prompt("Paste a YouTube video URL:");
    if (!url) return;
    const videoId = extractYouTubeId(url.trim());
    if (!videoId) { alert("Could not find a valid YouTube video ID in that URL."); return; }
    editor.chain().focus().setYouTube({ videoId, src: url.trim() }).run();
  }, [editor]);

  // CTA Button embed — open modal
  const handleInsertButton = useCallback(() => {
    if (!editor) return;
    setCtaModal({ label: "Register Now", url: "", linkType: "url" });
    if (ctaPages === null) {
      fetchWebPages({ pageType: "landing", status: "published" })
        .then((data) => setCtaPages(data.pages || []))
        .catch(() => setCtaPages([]));
    }
  }, [editor, ctaPages]);

  // Poll embed
  const handleInsertPoll = useCallback(() => {
    if (!editor) return;
    const qCountStr = window.prompt("How many polling questions?", "1");
    if (!qCountStr) return;
    const qCount = parseInt(qCountStr, 10);
    if (isNaN(qCount) || qCount < 1) { alert("Please enter at least 1."); return; }

    const polls = [];
    for (let q = 1; q <= qCount; q++) {
      const label = qCount > 1 ? `Question ${q}: ` : "";
      const question = window.prompt(`${label}Polling question:`, "What do you think?");
      if (question === null) return;
      if (!question.trim()) { alert("Question cannot be empty."); return; }

      const optCountStr = window.prompt(`${label}How many options?`, "3");
      if (!optCountStr) return;
      const optCount = parseInt(optCountStr, 10);
      if (isNaN(optCount) || optCount < 2) { alert("Please enter at least 2 options."); return; }

      const options = [];
      for (let i = 1; i <= optCount; i++) {
        const opt = window.prompt(`${label}Option ${i}:`);
        if (opt === null) return;
        const trimmed = opt.trim();
        if (!trimmed) { alert(`Option ${i} cannot be empty.`); return; }
        options.push(trimmed);
      }

      const allowFreeText = window.confirm(
        `${label}Press OK to add a free-text response option, or Cancel to skip.`,
      );
      polls.push({ question: question.trim(), options, allowFreeText });
    }

    for (const poll of polls) {
      editor.chain().focus().setPoll(poll).run();
    }
  }, [editor]);

  // Webcast registration link
  const handleInsertWebcastLink = useCallback(async () => {
    if (!editor) return;
    try {
      const { events } = await fetchZoomEvents({ daysAhead: 90 });
      if (!events || events.length === 0) {
        alert("No upcoming webcasts found. Create one first.");
        return;
      }
      const options = events.map((e, i) => `${i + 1}. ${e.title} (${new Date(e.scheduledAt).toLocaleDateString("en-GB")})`);
      const choice = window.prompt(
        `Select a webcast:\n\n${options.join("\n")}\n\nEnter number:`,
        "1",
      );
      if (!choice) return;
      const idx = parseInt(choice, 10) - 1;
      if (isNaN(idx) || idx < 0 || idx >= events.length) {
        alert("Invalid selection.");
        return;
      }
      const event = events[idx];
      const tag = `{{webcast_register:${event.id}}}`;
      // Insert as a CTA button with the merge tag as href
      editor.chain().focus().insertContent(
        `<a href="${tag}" style="display:inline-block;padding:12px 32px;background:#213D61;color:#ffffff;text-decoration:none;font-weight:700;border-radius:6px;font-size:15px;">Register for ${event.title} &rarr;</a>`,
      ).run();
    } catch (err) {
      alert("Failed to load webcasts: " + err.message);
    }
  }, [editor]);

  // Drag-and-drop visual state
  const handleEditorDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleSave = useCallback(async () => {
    if (!editor || saving) return null;
    setSaving(true);
    try {
      const mjml = (selectedTemplate?.mjml || "").replace("{{PREHEADER}}", preheader || "");
      const result = await saveNewsletterDraft({
        id: draftId || undefined,
        title: title || subject,
        subject,
        preheader,
        templateId: selectedTemplate?.id || "blank",
        bodyHtml: editor.getHTML(),
        segment,
        customEmails: segment === "custom" ? customEmails : [],
        mjmlTemplate: mjml,
      });
      if (result.id && result.id !== draftId) {
        setDraftId(result.id);
        setSearchParams({ draft: result.id }, { replace: true });
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      return result;
    } catch (err) {
      console.error("Save failed:", err);
      alert(`Save failed: ${err.message}`);
      return null;
    } finally {
      setSaving(false);
    }
  }, [editor, saving, draftId, title, subject, preheader, selectedTemplate, segment, customEmails, setSearchParams]);

  // Send a test email to the logged-in user
  const handleTestSend = useCallback(async () => {
    if (!editor || !selectedTemplate || testSending) return;
    setTestSending(true);
    setTestSent(null);
    try {
      const mjml = selectedTemplate.mjml.replace("{{PREHEADER}}", preheader || "");
      const useO365 = tenant.features?.o365;
      let graphToken = null;
      if (useO365) graphToken = await getGraphToken();
      const result = await testSendNewsletter({
        subject,
        mjmlTemplate: mjml,
        bodyHtml: editor.getHTML(),
        sendMethod: useO365 ? "o365" : "sendgrid",
        graphToken,
      });
      setTestSent(result);
      setTimeout(() => setTestSent(null), 5000);
    } catch (err) {
      alert(`Test send failed: ${err.message}`);
    } finally {
      setTestSending(false);
    }
  }, [editor, selectedTemplate, subject, testSending, tenant]);

  // MJML preview compilation
  const getCompiledEmailHtml = useCallback(() => {
    if (!editor || !selectedTemplate) return "";
    let bodyHtml = editor.getHTML();

    MERGE_TAGS.forEach((mt) => {
      const cleanTag = mt.tag.replace(/[{}]/g, "");
      const spanPattern = new RegExp(
        `<span[^>]*data-merge-tag="${cleanTag}"[^>]*>[^<]*</span>`, "gi",
      );
      bodyHtml = bodyHtml.replace(spanPattern, mt.example);
      bodyHtml = bodyHtml.replace(
        new RegExp(mt.tag.replace(/[{}]/g, "\\$&"), "g"), mt.example,
      );
    });

    bodyHtml = bodyHtml.replace(/<img /g, '<img style="max-width:100%;height:auto;display:block;" ');

    const mjmlString = selectedTemplate.mjml
      .replace("{{PREHEADER}}", preheader || "")
      .replace("{{BODY}}", bodyHtml)
      .replace(/\{\{web_version_link\}\}/g, "#");
    try {
      const { html, errors } = mjml2html(mjmlString, { validationLevel: "soft" });
      if (errors?.length) console.warn("MJML warnings:", errors);
      return html;
    } catch (err) {
      console.error("MJML compilation error:", err);
      return `<html><body style="color:red;padding:20px;">MJML Error: ${err.message}</body></html>`;
    }
  }, [editor, selectedTemplate]);

  // Auto-resize iframe to content
  const handleIframeLoad = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    try {
      const doc = iframe.contentDocument;
      if (doc?.body) iframe.style.height = doc.body.scrollHeight + 20 + "px";
    } catch { /* Cross-origin safety */ }
  }, []);

  // Block format handler
  const handleBlockFormat = useCallback(
    (value) => {
      if (!editor) return;
      if (value === "p") editor.chain().focus().setParagraph().run();
      else if (value === "h1") editor.chain().focus().toggleHeading({ level: 1 }).run();
      else if (value === "h2") editor.chain().focus().toggleHeading({ level: 2 }).run();
      else if (value === "h3") editor.chain().focus().toggleHeading({ level: 3 }).run();
      else if (value === "blockquote") editor.chain().focus().toggleBlockquote().run();
    },
    [editor],
  );

  const currentBlock = editor?.isActive("heading", { level: 1 })
    ? "h1"
    : editor?.isActive("heading", { level: 2 })
      ? "h2"
      : editor?.isActive("heading", { level: 3 })
        ? "h3"
        : editor?.isActive("blockquote")
          ? "blockquote"
          : "p";

  const previewWidth = previewMode === "mobile" ? 375 : 640;

  const previewSubject = MERGE_TAGS.reduce(
    (s, mt) => s.replace(new RegExp(mt.tag.replace(/[{}]/g, "\\$&"), "g"), mt.example),
    subject,
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        background: colors.bg,
        fontFamily: fonts.body,
      }}
    >
      <EditorTopBar
        navigate={navigate}
        previewMode={previewMode}
        setPreviewMode={setPreviewMode}
        saving={saving}
        saved={saved}
        handleSave={handleSave}
        testSending={testSending}
        testSent={testSent}
        handleTestSend={handleTestSend}
        setShowSendModal={setShowSendModal}
      />

      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
        <EditorSidebar
          segment={segment}
          setSegment={setSegment}
          segmentCounts={segmentCounts}
          audienceLists={audienceLists}
          customEmails={customEmails}
          setCustomEmails={setCustomEmails}
          customEmailInput={customEmailInput}
          setCustomEmailInput={setCustomEmailInput}
          title={title}
          setTitle={setTitle}
          subject={subject}
          setSubject={setSubject}
          preheader={preheader}
          setPreheader={setPreheader}
          templates={templates}
          selectedTemplate={selectedTemplate}
          setSelectedTemplate={setSelectedTemplate}
          insertMergeTag={insertMergeTag}
        />

        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {!previewMode && editor && (
            <EditorToolbar
              editor={editor}
              currentBlock={currentBlock}
              handleBlockFormat={handleBlockFormat}
              showColorPicker={showColorPicker}
              setShowColorPicker={setShowColorPicker}
              showMergeTags={showMergeTags}
              setShowMergeTags={setShowMergeTags}
              insertMergeTag={insertMergeTag}
              insertLink={insertLink}
              handleInsertImage={handleInsertImage}
              handleInsertYouTube={handleInsertYouTube}
              handleInsertButton={handleInsertButton}
              handleInsertPoll={handleInsertPoll}
              handleInsertWebcastLink={handleInsertWebcastLink}
              uploading={uploading}
            />
          )}

          <div
            style={{
              flex: 1,
              overflow: "auto",
              display: "flex",
              justifyContent: "center",
              padding: previewMode ? "32px 20px" : "24px 20px",
              background: previewMode ? "#f1f3f5" : "#e8eaed",
            }}
          >
            {previewMode ? (
              <EditorPreview
                previewMode={previewMode}
                previewWidth={previewWidth}
                previewSubject={previewSubject}
                tenant={tenant}
                iframeRef={iframeRef}
                getCompiledEmailHtml={getCompiledEmailHtml}
                handleIframeLoad={handleIframeLoad}
              />
            ) : (
              <div
                style={{
                  width: "100%",
                  maxWidth: 720,
                  background: "#ffffff",
                  borderRadius: 8,
                  boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                  alignSelf: "flex-start",
                  ...(spacing && {
                    "--spacing-h1": `${spacing.h1Above ?? 24}px`,
                    "--spacing-h1-after": `${spacing.h1Below ?? 12}px`,
                    "--spacing-h2": `${spacing.h2Above ?? 20}px`,
                    "--spacing-h2-after": `${spacing.h2Below ?? 10}px`,
                    "--spacing-h3": `${spacing.h3Above ?? 16}px`,
                    "--spacing-h3-after": `${spacing.h3Below ?? 8}px`,
                    "--spacing-p": `${spacing.pBelow ?? 12}px`,
                  }),
                }}
                onDrop={handleEditorDrop}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                className={dragOver ? "editor-drop-active" : ""}
              >
                {uploading && (
                  <div
                    style={{
                      padding: "8px 16px",
                      background: colors.accentDim,
                      borderRadius: 6,
                      marginBottom: 12,
                      fontSize: 12,
                      color: colors.accent,
                      textAlign: "center",
                    }}
                  >
                    Uploading image...
                  </div>
                )}
                <EditorContent editor={editor} />
              </div>
            )}
          </div>

          <EditorStatusBar
            wordCount={wordCount}
            segment={segment}
            customEmails={customEmails}
            segmentCounts={segmentCounts}
            editor={editor}
          />
        </div>
      </div>

      <CtaButtonModal
        ctaModal={ctaModal}
        setCtaModal={setCtaModal}
        ctaPages={ctaPages}
        editor={editor}
      />

      {showSendModal && (
        <SendConfirmModal
          draftId={draftId}
          subject={subject}
          preheader={preheader}
          bodyHtml={editor?.getHTML() || ""}
          segment={segment}
          customEmails={customEmails}
          segmentCounts={segmentCounts}
          audienceLists={audienceLists}
          tenant={tenant}
          onClose={() => setShowSendModal(false)}
          onSent={() => navigate("/newsletters")}
        />
      )}
    </div>
  );
}
